import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { useSelectedWorkspaceContext } from "common/helpers/SelectedWorkspaceContext";

import { HTTPError, patch } from "../helpers/HTTP";
import getQueryKeys from "./getQueryKeys";
import useWorkspaceCacheUpdate from "./useWorkspaceCacheUpdate";

async function addRentalAccountsRequest(
  workspaceId: string,
  rentalCount: number,
  planPriceId?: string,
) {
  await patch(`workspaces/${workspaceId}/rental`, {
    rental_count: rentalCount,
    item_price_id: planPriceId,
  });
}

export default function useRentalMutations() {
  const queryClient = useQueryClient();

  const { id: workspaceId, rental_subscription } =
    useSelectedWorkspaceContext();

  const { updateCachedWorkspace } = useWorkspaceCacheUpdate();

  const { mutateAsync: addRentalAccounts, isPending: isAddingRentalAccounts } =
    useMutation({
      mutationFn: ({
        rentalCount,
        planPriceId,
      }: {
        rentalCount: number;
        planPriceId?: string;
      }) => addRentalAccountsRequest(workspaceId, rentalCount, planPriceId),
      onSuccess: (_, { rentalCount }) => {
        updateCachedWorkspace(workspaceId, (draftWorkspace) => {
          const { rental_subscription } = draftWorkspace;

          if (rental_subscription) {
            rental_subscription.rented_count = rentalCount;
            rental_subscription.status = "active";
          }
        });

        queryClient.invalidateQueries({
          queryKey:
            getQueryKeys(workspaceId).estimate.renew(rental_subscription),
        });

        toast.success("Added rented account(s)");
      },
      onError: (error) =>
        toast.error(
          error instanceof HTTPError && error.serverMessage
            ? error.serverMessage
            : "Failed to add rented account(s)",
        ),
    });

  return {
    addRentalAccounts,
    isAddingRentalAccounts,
  };
}
